import { useEffect, useState, useCallback } from "react";
import { ChevronRight, Loader2, Save, UploadCloud, Eye, CheckCircle2, Lock, BookOpen } from "lucide-react";
import {
    fetchTradePlaybooks, fetchTradePlaybook, patchTradePlaybook,
    publishTradePlaybook, previewTradePlaybook, formatApiError,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

function StatusBadge({ status }) {
    const map = {
        published: { l: "Publiceret", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
        draft: { l: "Kladde", c: "bg-amber-50 text-amber-700 border-amber-200" },
        not_configured: { l: "Ikke konfigureret", c: "bg-slate-100 text-slate-500 border-slate-200" },
    };
    const s = map[status] || map.draft;
    return <Badge className={`border ${s.c}`} data-testid={`playbook-status-${status}`}>{s.l}</Badge>;
}

function TradeEditor({ trade, sectionKeys, sectionDescriptions, onSaved }) {
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({});
    const [meta, setMeta] = useState(null);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [preview, setPreview] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await fetchTradePlaybook(trade);
            setDraft(d.draft || {});
            setMeta(d);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [trade]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const d = await patchTradePlaybook(trade, draft);
            setMeta((m) => ({ ...m, ...d }));
            toast.success("Gemt");
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const publish = async () => {
        setPublishing(true);
        try {
            await save();
            const res = await publishTradePlaybook(trade);
            toast.success(`Publiceret — version ${res.playbook_version}`);
            await load();
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPublishing(false); }
    };

    const loadPreview = async () => {
        try { setPreview(await previewTradePlaybook(trade)); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading) return <div className="flex items-center gap-2 text-slate-500 py-8 px-5"><Loader2 className="w-4 h-4 animate-spin" /> Indlæser…</div>;

    return (
        <div className="px-5 pb-5 space-y-4" data-testid={`playbook-editor-${trade}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <div className="flex items-center gap-3 text-xs text-slate-500">
                    <StatusBadge status={meta?.status} />
                    <span>Version <b className="text-slate-700">{meta?.playbook_version || 0}</b></span>
                    {meta?.has_unpublished_changes && <span className="text-amber-600">Ikke-publicerede ændringer</span>}
                    {meta?.published_at && <span>Publiceret: {new Date(meta.published_at).toLocaleString("da-DK")}</span>}
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={loadPreview} data-testid={`playbook-preview-${trade}`}><Eye className="w-4 h-4 mr-1.5" />Preview</Button>
                    <Button variant="outline" size="sm" onClick={save} disabled={saving} data-testid={`playbook-save-${trade}`}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}Gem
                    </Button>
                    <Button size="sm" onClick={publish} disabled={publishing} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`playbook-publish-${trade}`}>
                        {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <UploadCloud className="w-4 h-4 mr-1.5" />}Publicér
                    </Button>
                </div>
            </div>

            {(sectionKeys || []).map((k) => (
                <div key={k} className="space-y-1.5" data-testid={`playbook-section-${trade}-${k}`}>
                    <Label className="text-sm font-medium text-slate-800 capitalize">{k.replace(/_/g, " ")}</Label>
                    {sectionDescriptions?.[k] && <p className="text-xs text-slate-500">{sectionDescriptions[k]}</p>}
                    <Textarea rows={4} value={draft[k] || ""}
                        onChange={(e) => setDraft((p) => ({ ...p, [k]: e.target.value }))}
                        data-testid={`playbook-input-${trade}-${k}`} className="font-mono text-xs" />
                </div>
            ))}

            {preview && (
                <div data-testid={`playbook-preview-panel-${trade}`}>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                        {preview.published ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Publiceret v{preview.playbook_version}</> : <span className="text-amber-600">Ikke publiceret endnu</span>}
                    </div>
                    <pre className="bg-slate-900 text-slate-100 text-[11px] rounded-xl p-4 overflow-auto max-h-96" data-testid={`playbook-preview-json-${trade}`}>
{JSON.stringify(preview.payload, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
}

export default function AdminAiBrancher() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);

    const load = useCallback(async () => {
        try { setData(await fetchTradePlaybooks()); }
        catch (e) { toast.error(formatApiError(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="flex items-center gap-2 text-slate-500 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Indlæser…</div>;

    return (
        <div className="max-w-4xl" data-testid="admin-ai-brancher">
            <div className="mb-6">
                <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    <h1 className="text-xl font-bold text-slate-900">AI-brancher</h1>
                    <span className="text-[10px] uppercase tracking-wider bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full font-bold">Kun super-admin</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">Centrale, versionerede branche-playbooks. Kun publicerede playbooks bruges af agenten. Ændringer indsættes IKKE automatisk i nogen prompt.</p>
            </div>

            <div className="space-y-3">
                {data.trades.map((t) => {
                    const isOpen = open === t.trade_slug;
                    return (
                        <div key={t.trade_slug} className="border border-slate-200 rounded-2xl bg-white overflow-hidden">
                            <button
                                onClick={() => t.implemented && setOpen(isOpen ? null : t.trade_slug)}
                                disabled={!t.implemented}
                                data-testid={`playbook-card-${t.trade_slug}`}
                                className={`w-full flex items-center justify-between px-5 py-4 ${t.implemented ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}>
                                <div className="flex items-center gap-3">
                                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""} ${!t.implemented ? "opacity-0" : ""}`} />
                                    <span className="text-sm font-semibold text-slate-900">{t.name}</span>
                                    {t.label && t.label !== t.name && <span className="text-xs text-slate-400">({t.label})</span>}
                                </div>
                                <div className="flex items-center gap-3">
                                    {t.implemented && t.playbook_version > 0 && <span className="text-xs text-slate-400">v{t.playbook_version}</span>}
                                    {t.implemented ? <StatusBadge status={t.status} /> : (
                                        <span className="flex items-center gap-1.5 text-xs text-slate-400"><Lock className="w-3 h-3" />Ikke konfigureret</span>
                                    )}
                                </div>
                            </button>
                            {isOpen && t.implemented && (
                                <div className="border-t border-slate-100">
                                    <TradeEditor trade={t.trade_slug} sectionKeys={data.section_keys}
                                        sectionDescriptions={data.section_descriptions} onSaved={load} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
